import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Copy, Check } from 'lucide-react';
import Modal from '../Modal';
import { ticketsApi } from '../../api/tickets';

interface AIPromptModalProps {
  ticketId: string;
  ticketNumber: string;
  onClose: () => void;
}

/* Turns the ticket into a ready-to-paste prompt for an AI coding assistant
   (Claude Code in VS Code / terminal). The server assembles it; the text is
   editable here so staff can trim or add context before copying. Nothing is
   sent anywhere — copy/paste only. */
export default function AIPromptModal({ ticketId, ticketNumber, onClose }: AIPromptModalProps) {
  const [text, setText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket-ai-prompt', ticketId],
    queryFn: () => ticketsApi.aiPrompt(ticketId),
  });

  // Seed once per open — a background refetch must not clobber manual edits.
  useEffect(() => {
    if (data !== undefined && text === null) setText(data);
  }, [data, text]);

  const handleCopy = async () => {
    if (text === null) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Prompt copied — paste it into Claude Code');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not access the clipboard — select the text and copy manually');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`AI prompt — ${ticketNumber}`}
      width={720}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCopy}
            disabled={text === null}
          >
            {copied
              ? <><Check className="w-3.5 h-3.5" /> Copied</>
              : <><Copy className="w-3.5 h-3.5" /> Copy prompt</>}
          </button>
        </>
      }
    >
      <p className="text-sm text-muted mb-3">
        This ticket, formatted as a prompt for an AI coding assistant. Edit if
        needed, then copy and paste it into Claude Code opened in the
        product&rsquo;s repository.
      </p>

      {isLoading && text === null && (
        <div className="py-10 text-center text-muted text-sm">Building prompt…</div>
      )}
      {!!error && text === null && (
        <div className="py-10 text-center text-err text-sm">
          {(error as Error).message || 'Failed to build the prompt'}
        </div>
      )}

      {text !== null && (
        <textarea
          className="input w-full font-mono text-xs leading-relaxed"
          style={{ minHeight: '50vh', resize: 'vertical' }}
          value={text}
          onChange={e => setText(e.target.value)}
          spellCheck={false}
        />
      )}
    </Modal>
  );
}
